export type CallDetailsRouteConfig = {
  routePath: string;
  returnPath: string;
  returnLabel: string;
  permissions: string[];
};

// Route prefix constants - extracted from routePath values
export const CALL_DETAILS_ROUTE_PREFIXES = {
  COMPANY_PROFILE: 'call-company-profile-logs',
  COMPANY: 'call-company-logs',
  INDIVIDUAL: 'call-individual-logs',
  TASK: 'call-task-logs'
} as const;

export type CallDetailsRoutePrefix =
  typeof CALL_DETAILS_ROUTE_PREFIXES[keyof typeof CALL_DETAILS_ROUTE_PREFIXES];

export const callDetailsRoutes: CallDetailsRouteConfig[] = [
  {
    // route to page
    routePath: `${CALL_DETAILS_ROUTE_PREFIXES.COMPANY_PROFILE}/:id`,
    // route back to this page
    returnPath: '/company',
    returnLabel: 'Company',
    permissions: ['company_view', 'company_manage']
  },
  {
    routePath: `${CALL_DETAILS_ROUTE_PREFIXES.COMPANY}/:id`,
    returnPath: '/company',
    returnLabel: 'Companies',
    permissions: ['company_view', 'company_manage']
  },
  {
    routePath: `${CALL_DETAILS_ROUTE_PREFIXES.INDIVIDUAL}/:id`,
    returnPath: '/individual',
    returnLabel: 'Individuals',
    permissions: ['individual_view', 'individual_manage']
  },
  {
    routePath: `${CALL_DETAILS_ROUTE_PREFIXES.TASK}/:id`,
    returnPath: '/tasks',
    returnLabel: 'Tasks',
    permissions: ['task_view', 'task_manage']
  }
];

/**
 * Maps a returnPath to the corresponding call details route path with call ID
 * @param returnPath - The return path from ActivityLog (e.g., '/companies', '/company/123')
 * @param callId - The call ID to navigate to
 * @param sourceId - Optional source ID to extract from returnPath for dynamic routes
 * @returns The corresponding call details route path with call ID or null if not found
 */
export function getCallDetailsRoute(
  returnPath: string,
  callId: string | number,
  sourceId?: number
): string | null {
  // Handle exact matches first
  const exactMatch = callDetailsRoutes.find(
    (route) => route.returnPath === returnPath
  );
  if (exactMatch) {
    return exactMatch.routePath.replace(':id', String(callId));
  }

  // Handle dynamic paths like /company/:id or /individual/:id
  if (returnPath.startsWith('/company/')) {
    return `call-details-company-detail/${callId}`;
  }
  if (returnPath.startsWith('/individual/')) {
    return `call-details-individual-detail/${callId}`;
  }
  if (returnPath.startsWith('/tasks/')) {
    return `call-details-task/${callId}`;
  }

  // Fallback: try to match by prefix
  if (returnPath.startsWith('/companies')) {
    return `call-details-company/${callId}`;
  }
  if (returnPath.startsWith('/individuals')) {
    return `call-details-individual/${callId}`;
  }

  return null;
}
